import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator as CalculatorIcon, Maximize2, Minimize2, X } from 'lucide-react';
import { ThemeContext } from '../ThemeContext';
import {
  CALCULATOR_VIEWPORT_MODE,
  getCalculatorViewportMode,
  getMobileCalculatorFrame,
} from '../utils/calculatorLayout';

const DESKTOP_MIN_WIDTH = 360;
const DESKTOP_MIN_HEIGHT = 480;
const MOBILE_MIN_WIDTH = 300;
const MOBILE_MIN_HEIGHT = 420;

const getViewport = () => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 1440,
  height: typeof window !== 'undefined' ? window.innerHeight : 900,
});

const Calculator = ({ isOpen, onClose, categoryType }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);
  const [expression, setExpression] = useState('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: DESKTOP_MIN_WIDTH, height: DESKTOP_MIN_HEIGHT });
  const [isMinimized, setIsMinimized] = useState(false);
  const [viewport, setViewport] = useState(getViewport);
  const calculatorRef = useRef(null);
  const isDraggingRef = useRef(false);

  const displayRef = useRef(display);
  const previousValueRef = useRef(previousValue);
  const operationRef = useRef(operation);
  const waitingForNewValueRef = useRef(waitingForNewValue);
  const expressionRef = useRef(expression);

  useEffect(() => {
    displayRef.current = display;
    previousValueRef.current = previousValue;
    operationRef.current = operation;
    waitingForNewValueRef.current = waitingForNewValue;
    expressionRef.current = expression;
  }, [display, previousValue, operation, waitingForNewValue, expression]);

  useEffect(() => {
    const handleResize = () => {
      setViewport(getViewport());
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const categoryTypeLower = categoryType?.toLowerCase() || '';
  const isMathCategory =
    categoryTypeLower === 'math' ||
    categoryTypeLower === 'all math' ||
    categoryTypeLower.includes('math');

  const viewportMode = useMemo(
    () => getCalculatorViewportMode(viewport.width, viewport.height),
    [viewport.height, viewport.width],
  );
  const isMobileLayout = viewportMode !== CALCULATOR_VIEWPORT_MODE.DESKTOP;
  const isLandscapeMobile = viewportMode === CALCULATOR_VIEWPORT_MODE.MOBILE_LANDSCAPE;
  const mobileFrame = useMemo(
    () => getMobileCalculatorFrame(viewportMode, viewport.width, viewport.height, isMinimized),
    [isMinimized, viewport.height, viewport.width, viewportMode],
  );

  useEffect(() => {
    if (!isOpen || isMobileLayout) {
      return;
    }

    const initialX = Math.max(20, window.innerWidth - DESKTOP_MIN_WIDTH - 20);
    const initialY = 100;
    setPosition({ x: initialX, y: initialY });
  }, [isMobileLayout, isOpen]);

  useEffect(() => {
    if (!isOpen || !isMobileLayout || isMinimized) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMinimized, isMobileLayout, isOpen]);

  const getOperationSymbol = (nextOperation) => {
    switch (nextOperation) {
      case '+':
        return '+';
      case '-':
        return '−';
      case '*':
        return '×';
      case '/':
        return '÷';
      default:
        return nextOperation;
    }
  };

  const calculate = (firstValue, secondValue, currentOperation) => {
    switch (currentOperation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '*':
        return firstValue * secondValue;
      case '/':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      case '=':
        return secondValue;
      default:
        return secondValue;
    }
  };

  const handleNumber = (num) => {
    if (waitingForNewValue) {
      setDisplay(String(num));
      setWaitingForNewValue(false);
      if (previousValue !== null && operation) {
        setExpression(`${previousValue} ${getOperationSymbol(operation)} ${num}`);
      }
      return;
    }

    const newDisplay = display === '0' ? String(num) : `${display}${num}`;
    setDisplay(newDisplay);
    if (previousValue !== null && operation) {
      setExpression(`${previousValue} ${getOperationSymbol(operation)} ${newDisplay}`);
    }
  };

  const handleDecimal = () => {
    if (waitingForNewValue) {
      setDisplay('0.');
      setWaitingForNewValue(false);
      if (previousValue !== null && operation) {
        setExpression(`${previousValue} ${getOperationSymbol(operation)} 0.`);
      }
      return;
    }

    if (display.indexOf('.') === -1) {
      const newDisplay = `${display}.`;
      setDisplay(newDisplay);
      if (previousValue !== null && operation) {
        setExpression(`${previousValue} ${getOperationSymbol(operation)} ${newDisplay}`);
      }
    }
  };

  const handleOperation = (nextOperation) => {
    const inputValue = parseFloat(display) || 0;

    if (previousValue === null) {
      setPreviousValue(inputValue);
      setExpression(`${inputValue} ${getOperationSymbol(nextOperation)}`);
    } else if (operation) {
      const newValue = calculate(previousValue || 0, inputValue, operation);
      setDisplay(String(newValue));
      setPreviousValue(newValue);
      setExpression(`${newValue} ${getOperationSymbol(nextOperation)}`);
    } else {
      setPreviousValue(inputValue);
      setExpression(`${inputValue} ${getOperationSymbol(nextOperation)}`);
    }

    setWaitingForNewValue(true);
    setOperation(nextOperation);
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);
    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      setDisplay(String(newValue));
      setExpression(`${previousValue} ${getOperationSymbol(operation)} ${inputValue} = ${newValue}`);
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(true);
    }
  };

  const handleClear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
    setExpression('');
  };

  const handleBackspace = () => {
    if (waitingForNewValue) {
      setOperation(null);
      setPreviousValue(null);
      setExpression('');
      setWaitingForNewValue(false);
      return;
    }

    if (display.length > 1) {
      const newDisplay = display.slice(0, -1);
      setDisplay(newDisplay);
      if (previousValue !== null && operation) {
        setExpression(`${previousValue} ${getOperationSymbol(operation)} ${newDisplay}`);
      }
      return;
    }

    setDisplay('0');
    if (previousValue !== null && operation) {
      setExpression(`${previousValue} ${getOperationSymbol(operation)} 0`);
    }
  };

  const handleMouseDown = (e) => {
    if (isMobileLayout) {
      return;
    }

    const header = e.target.closest('.calculator-header');
    const isButton = e.target.closest('button');
    const isResizeHandle = e.target.closest('.resize-handle');

    if (!header || isButton || isResizeHandle) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    isDraggingRef.current = true;
    const startX = e.clientX - position.x;
    const startY = e.clientY - position.y;

    const handleMouseMove = (event) => {
      if (!isDraggingRef.current) {
        return;
      }

      const newX = event.clientX - startX;
      const newY = event.clientY - startY;
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - (isMinimized ? 60 : size.height);

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && calculatorRef.current && !calculatorRef.current.contains(event.target)) {
        if (!event.target.closest('button[title="Open Calculator"]')) {
          return;
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable
      ) {
        return;
      }

      const currentDisplay = displayRef.current;
      const currentPreviousValue = previousValueRef.current;
      const currentOperation = operationRef.current;
      const currentWaitingForNewValue = waitingForNewValueRef.current;

      if (/[0-9+\-*/.=()]/.test(event.key) || event.key === 'Enter' || event.key === 'Backspace' || event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }

      if (/[0-9]/.test(event.key)) {
        const num = parseInt(event.key, 10);
        if (currentWaitingForNewValue) {
          setDisplay(String(num));
          setWaitingForNewValue(false);
          if (currentPreviousValue !== null && currentOperation) {
            setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} ${num}`);
          }
          return;
        }

        const newDisplay = currentDisplay === '0' ? String(num) : `${currentDisplay}${num}`;
        setDisplay(newDisplay);
        if (currentPreviousValue !== null && currentOperation) {
          setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} ${newDisplay}`);
        }
        return;
      }

      if (event.key === '.' || event.key === ',') {
        if (currentWaitingForNewValue) {
          setDisplay('0.');
          setWaitingForNewValue(false);
          if (currentPreviousValue !== null && currentOperation) {
            setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} 0.`);
          }
          return;
        }

        if (currentDisplay.indexOf('.') === -1) {
          const newDisplay = `${currentDisplay}.`;
          setDisplay(newDisplay);
          if (currentPreviousValue !== null && currentOperation) {
            setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} ${newDisplay}`);
          }
        }
        return;
      }

      if (['+', '-', '*', '/'].includes(event.key)) {
        const inputValue = parseFloat(currentDisplay) || 0;
        if (currentPreviousValue === null) {
          setPreviousValue(inputValue);
          setExpression(`${inputValue} ${getOperationSymbol(event.key)}`);
        } else if (currentOperation) {
          const newValue = calculate(currentPreviousValue, inputValue, currentOperation);
          setDisplay(String(newValue));
          setPreviousValue(newValue);
          setExpression(`${newValue} ${getOperationSymbol(event.key)}`);
        } else {
          setPreviousValue(inputValue);
          setExpression(`${inputValue} ${getOperationSymbol(event.key)}`);
        }
        setWaitingForNewValue(true);
        setOperation(event.key);
        return;
      }

      if (event.key === '(') {
        if (currentWaitingForNewValue || currentDisplay === '0') {
          setDisplay('(');
          setWaitingForNewValue(false);
        } else {
          setDisplay(`${currentDisplay}(`);
        }
        return;
      }

      if (event.key === ')' && currentDisplay.indexOf('(') !== -1) {
        setDisplay(`${currentDisplay})`);
        return;
      }

      if (event.key === '=' || event.key === 'Enter') {
        const inputValue = parseFloat(currentDisplay);
        if (currentPreviousValue !== null && currentOperation) {
          const newValue = calculate(currentPreviousValue, inputValue, currentOperation);
          setDisplay(String(newValue));
          setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} ${inputValue} = ${newValue}`);
          setPreviousValue(null);
          setOperation(null);
          setWaitingForNewValue(true);
        }
        return;
      }

      if (event.key === 'Escape' || (event.key === 'c' && event.ctrlKey)) {
        handleClear();
        return;
      }

      if (event.key === 'Backspace') {
        if (currentWaitingForNewValue) {
          setOperation(null);
          setPreviousValue(null);
          setExpression('');
          setWaitingForNewValue(false);
        } else if (currentDisplay.length > 1) {
          const newDisplay = currentDisplay.slice(0, -1);
          setDisplay(newDisplay);
          if (currentPreviousValue !== null && currentOperation) {
            setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} ${newDisplay}`);
          }
        } else {
          setDisplay('0');
          if (currentPreviousValue !== null && currentOperation) {
            setExpression(`${currentPreviousValue} ${getOperationSymbol(currentOperation)} 0`);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  const handleResize = (e, direction) => {
    if (isMobileLayout) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;
    const startLeft = position.x;
    const startTop = position.y;

    const handleMouseMove = (event) => {
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startLeft;
      let newY = startTop;

      const minWidth = viewport.width < 640 ? MOBILE_MIN_WIDTH : DESKTOP_MIN_WIDTH;
      const minHeight = viewport.width < 640 ? MOBILE_MIN_HEIGHT : DESKTOP_MIN_HEIGHT;

      if (direction.includes('right')) {
        newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, viewport.width - startLeft));
      }
      if (direction.includes('left')) {
        const widthChange = startWidth - deltaX;
        if (widthChange >= minWidth && startLeft + deltaX >= 0) {
          newWidth = widthChange;
          newX = startLeft + deltaX;
        }
      }
      if (direction.includes('bottom')) {
        newHeight = Math.max(minHeight, Math.min(startHeight + deltaY, viewport.height - startTop));
      }
      if (direction.includes('top')) {
        const heightChange = startHeight - deltaY;
        if (heightChange >= minHeight && startTop + deltaY >= 0) {
          newHeight = heightChange;
          newY = startTop + deltaY;
        }
      }

      const maxX = viewport.width - newWidth;
      const maxY = viewport.height - newHeight;

      setSize({
        width: Math.min(newWidth, viewport.width),
        height: Math.min(newHeight, viewport.height),
      });
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (!isMathCategory || !isOpen) {
    return null;
  }

  const isCompactLayout = isMobileLayout;
  const mobileButtonClass = isLandscapeMobile ? 'px-1.5 py-1.5 text-xs' : 'px-2 py-2 text-sm';
  const desktopButtonClass = 'px-2 py-2 sm:px-3 sm:py-2.5 text-sm sm:text-base';

  const buttonClass = `calculator-button ${
    isCompactLayout ? mobileButtonClass : desktopButtonClass
  } font-semibold rounded-lg transition-all duration-150 active:scale-95 ${
    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
  }`;

  const operatorButtonClass = `calculator-button ${
    isCompactLayout ? mobileButtonClass : desktopButtonClass
  } font-semibold rounded-lg transition-all duration-150 active:scale-95 bg-[#ff9e42] hover:bg-[#ff8a2e] text-white`;

  const equalsButtonClass = `calculator-button ${
    isCompactLayout ? mobileButtonClass : desktopButtonClass
  } font-semibold rounded-lg transition-all duration-150 active:scale-95 bg-[#34c759] hover:bg-[#2fb350] text-white`;

  const renderCalculatorContent = () => (
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} h-full flex flex-col`}>
      <div
        className={`calculator-header flex items-center justify-between px-4 py-2 ${
          isMobileLayout ? 'cursor-default' : 'cursor-move'
        } ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
      >
        <div className="flex items-center gap-2">
          <CalculatorIcon className="w-5 h-5" style={{ color: '#ff9e42' }} />
          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Calculator</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized((currentValue) => !currentValue)}
            className={`p-1 rounded hover:bg-gray-600 ${isDarkMode ? 'text-white' : 'text-gray-600'}`}
            aria-label={isMinimized ? 'Expand calculator' : 'Minimize calculator'}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-red-500 hover:text-white ${isDarkMode ? 'text-white' : 'text-gray-600'}`}
            aria-label="Close calculator"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className={`${isLandscapeMobile ? 'px-3 py-2' : isCompactLayout ? 'px-3 py-3' : 'px-3 sm:px-4 py-4 sm:py-6'} ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {expression && (
              <div
                className={`text-right ${isLandscapeMobile ? 'text-[11px]' : isCompactLayout ? 'text-xs' : 'text-sm sm:text-base'} font-mono mb-1 overflow-x-auto ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                {expression}
              </div>
            )}
            <div
              className={`text-right ${isLandscapeMobile ? 'text-xl' : isCompactLayout ? 'text-2xl' : 'text-2xl sm:text-3xl'} font-mono font-bold overflow-x-auto ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {display.length > 12 ? parseFloat(display).toExponential(6) : display}
            </div>
          </div>

          <div className={`grid grid-cols-5 ${isLandscapeMobile ? 'gap-1 p-2.5' : isCompactLayout ? 'gap-1.5 p-3' : 'gap-1.5 sm:gap-2 p-3 sm:p-4'}`}>
            <button onClick={handleClear} className={buttonClass}>C</button>
            <button onClick={handleBackspace} className={buttonClass}>⌫</button>
            <button
              onClick={() => {
                if (waitingForNewValue || display === '0') {
                  setDisplay('(');
                  setWaitingForNewValue(false);
                } else {
                  setDisplay(`${display}(`);
                }
              }}
              className={buttonClass}
            >
              (
            </button>
            <button
              onClick={() => {
                if (display.indexOf('(') !== -1) {
                  setDisplay(`${display})`);
                }
              }}
              className={buttonClass}
            >
              )
            </button>
            <button onClick={() => handleOperation('/')} className={operatorButtonClass}>÷</button>

            <button onClick={() => handleNumber(7)} className={buttonClass}>7</button>
            <button onClick={() => handleNumber(8)} className={buttonClass}>8</button>
            <button onClick={() => handleNumber(9)} className={buttonClass}>9</button>
            <button onClick={() => handleOperation('*')} className={operatorButtonClass}>×</button>
            <button onClick={() => handleOperation('-')} className={operatorButtonClass}>−</button>

            <button onClick={() => handleNumber(4)} className={buttonClass}>4</button>
            <button onClick={() => handleNumber(5)} className={buttonClass}>5</button>
            <button onClick={() => handleNumber(6)} className={buttonClass}>6</button>
            <button onClick={() => handleOperation('+')} className={operatorButtonClass}>+</button>
            <button onClick={handleEquals} className={`${equalsButtonClass} row-span-2`}>=</button>

            <button onClick={() => handleNumber(1)} className={buttonClass}>1</button>
            <button onClick={() => handleNumber(2)} className={buttonClass}>2</button>
            <button onClick={() => handleNumber(3)} className={buttonClass}>3</button>
            <button onClick={handleDecimal} className={buttonClass}>.</button>

            <button onClick={() => handleNumber(0)} className={`${buttonClass} col-span-2`}>0</button>
          </div>
        </div>
      )}

      {!isMobileLayout && !isMinimized && (
        <div
          className="resize-handle absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize opacity-50 hover:opacity-100 flex items-end justify-end z-10"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleResize(event, 'bottom-right');
          }}
        >
          <div
            className={`w-0 h-0 border-l-[12px] border-l-transparent border-b-[12px] ${
              isDarkMode ? 'border-b-gray-500' : 'border-b-gray-400'
            }`}
          />
        </div>
      )}
    </div>
  );

  if (isMobileLayout) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 flex justify-center ${
          mobileFrame?.align === 'end'
            ? `items-end ${isMinimized ? 'bg-transparent pointer-events-none' : 'bg-black/10'}`
            : `items-center ${isMinimized ? 'bg-transparent pointer-events-none' : 'bg-black/35'}`
        } p-3`}
      >
        <motion.div
          ref={calculatorRef}
          initial={{ opacity: 0, y: mobileFrame?.align === 'end' ? 24 : 0, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: mobileFrame?.align === 'end' ? 24 : 0, scale: 0.96 }}
          className="shadow-2xl rounded-xl overflow-hidden pointer-events-auto"
          style={{
            width: `${mobileFrame.width}px`,
            height: `${mobileFrame.height}px`,
            maxWidth: '100%',
            maxHeight: '100%',
            userSelect: 'none',
          }}
        >
          {renderCalculatorContent()}
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={calculatorRef}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed z-50 shadow-2xl rounded-xl overflow-hidden"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: isMinimized ? '60px' : `${size.height}px`,
        minWidth: viewport.width < 640 ? `${MOBILE_MIN_WIDTH}px` : `${DESKTOP_MIN_WIDTH}px`,
        minHeight: isMinimized ? '60px' : viewport.width < 640 ? `${MOBILE_MIN_HEIGHT}px` : `${DESKTOP_MIN_HEIGHT}px`,
        maxWidth: `${Math.min(viewport.width - position.x, viewport.width)}px`,
        maxHeight: `${Math.min(viewport.height - position.y, viewport.height)}px`,
        userSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
    >
      {renderCalculatorContent()}
    </motion.div>
  );
};

export default Calculator;
